import { Program, ProgramSlot } from '../types/program-types';
import type { SlotCategory } from './suggest-drills';

export const DEFAULT_SLOT_IDS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export const EXTENDED_SLOT_IDS = [
  'A1',
  'A2',
  'B1',
  'B2',
  'C1',
  'C2',
  'D1',
  'D2',
  'E1',
  'E2',
  'F1',
  'F2',
];

export const DEFAULT_SLOT_LABELS: Record<string, string> = {
  A1: 'Main Lift - Lower Push',
  A2: 'Main Lift - Upper Pull',
  B1: 'Accessory - Upper Push',
  B2: 'Accessory - Lower Pull',
  C1: 'Accessory - Core',
  C2: 'Accessory - Accessory Push/Pull',
};

export interface ProgramTemplateDefinition {
  id: string;
  name: string;
  dayNames: string[];
  description?: string;
}

// ---------------------------------------------------------------------------
// MVP Drill-Plan Templates (Introduction §3.1 and Full §3.2)
// ---------------------------------------------------------------------------

/** Per-slot metadata used for auto-fill and block-level drill picking. */
export interface DrillPlanSlotMeta {
  id: string;
  label: string;
  category: SlotCategory;
}

/** Extended template definition for the MVP automated driller. */
export interface DrillPlanTemplateDefinition {
  id: 'positional-intro' | 'small-drill-plan' | 'full';
  name: string;
  description: string;
  /** Allowed phase codes. positional-intro and small-drill-plan are P1-only. */
  allowedPhases: ('P1' | 'P2' | 'P3')[];
  dayNames: string[];
  /** Ordered slot definitions (same order applied to every day). */
  slots: DrillPlanSlotMeta[];
  /** "twice daily" flag for daily corrective templates. */
  twiceDaily?: boolean;
  /**
   * When set, auto-fill for this template will prefer drills in this Domain tag value.
   * e.g. 'Positional' ensures only positional/floor-based drills are selected.
   */
  preferredDomain?: string;
}

export const POSITIONAL_INTRO_SLOT_IDS = ['Pos-1', 'Pos-2', 'Pos-3'];

export const SMALL_DRILL_PLAN_SLOT_IDS = ['Drill-1', 'Drill-2', 'Drill-3'];

export const FULL_TEMPLATE_SLOT_IDS = [
  'Warm-Up-1',
  'Warm-Up-2',
  'Warm-Up-3',
  'A1',
  'A2',
  'B1',
  'B2',
  'C1',
  'C2',
  'C3',
  'D1',
];

/** One week of daily training (same 3 drills each day). */
export const DAILY_WEEK_DAY_NAMES = [
  'Day 1',
  'Day 2',
  'Day 3',
  'Day 4',
  'Day 5',
  'Day 6',
  'Day 7',
] as const;

/**
 * Positional Introduction — 3 positional/floor-based corrective drills, twice daily, every day.
 *
 * Rationale (docs/course-material/1 Course Handout Material/Week 7 Progression-Model-and-Positional-Drills.pdf):
 * Phase 1 addresses the global compensation pattern (APT or Swayback) using sidelying,
 * supine, or floor-based positional drills. Practitioners select 2–3 daily drills and
 * have clients perform them consistently for 1–2 weeks before re-testing.
 * This template enforces the Positional domain so auto-fill only picks floor-based
 * correctives (e.g. Sidelying Grounding, 90/90 Hip Lift, Half-Rolling).
 */
export const POSITIONAL_INTRO_TEMPLATE: DrillPlanTemplateDefinition = {
  id: 'positional-intro',
  name: 'Positional Introduction',
  description: '3 positional corrective drills, twice daily, every day for 1 week. Phase 1 only.',
  allowedPhases: ['P1'],
  dayNames: [...DAILY_WEEK_DAY_NAMES],
  twiceDaily: true,
  preferredDomain: 'Positional',
  slots: [
    { id: 'Pos-1', label: 'Positional Drill 1', category: 'main-corrective' },
    { id: 'Pos-2', label: 'Positional Drill 2', category: 'main-corrective' },
    { id: 'Pos-3', label: 'Positional Drill 3', category: 'main-corrective' },
  ],
};

/**
 * Small Drill Plan — 3 corrective drills (any domain), twice daily, every day for 1 week.
 * Formerly called "Introduction template" in the original spec (§3.1).
 */
export const SMALL_DRILL_PLAN_TEMPLATE: DrillPlanTemplateDefinition = {
  id: 'small-drill-plan',
  name: 'Small Drill Plan',
  description: '3 corrective drills, twice daily, every day for 1 week. Phase 1 only.',
  allowedPhases: ['P1'],
  dayNames: [...DAILY_WEEK_DAY_NAMES],
  twiceDaily: true,
  slots: [
    { id: 'Drill-1', label: 'Drill 1', category: 'main-corrective' },
    { id: 'Drill-2', label: 'Drill 2', category: 'main-corrective' },
    { id: 'Drill-3', label: 'Drill 3', category: 'main-corrective' },
  ],
};

export const FULL_TEMPLATE: DrillPlanTemplateDefinition = {
  id: 'full',
  name: 'Full',
  description: '11 slots per day, 3 days. Phases 1–3.',
  allowedPhases: ['P1', 'P2', 'P3'],
  dayNames: ['Day 1', 'Day 2', 'Day 3'],
  slots: [
    { id: 'Warm-Up-1', label: 'Warm-Up 1', category: 'warmup' },
    { id: 'Warm-Up-2', label: 'Warm-Up 2', category: 'warmup' },
    { id: 'Warm-Up-3', label: 'Warm-Up 3', category: 'warmup' },
    { id: 'A1', label: 'A1', category: 'main-corrective' },
    { id: 'A2', label: 'A2', category: 'main-corrective' },
    { id: 'B1', label: 'B1', category: 'accessory' },
    { id: 'B2', label: 'B2', category: 'accessory' },
    { id: 'C1', label: 'C1', category: 'accessory-secondary' },
    { id: 'C2', label: 'C2', category: 'accessory-secondary' },
    { id: 'C3', label: 'C3', category: 'accessory-secondary' },
    { id: 'D1', label: 'D1', category: 'cooldown' },
  ],
};

export const MVP_DRILL_PLAN_TEMPLATES: DrillPlanTemplateDefinition[] = [
  POSITIONAL_INTRO_TEMPLATE,
  SMALL_DRILL_PLAN_TEMPLATE,
  FULL_TEMPLATE,
];

export const MVP_TEMPLATE_MAP: Record<string, DrillPlanTemplateDefinition> =
  MVP_DRILL_PLAN_TEMPLATES.reduce((acc, t) => {
    acc[t.id] = t;
    return acc;
  }, {} as Record<string, DrillPlanTemplateDefinition>);

// ---------------------------------------------------------------------------
// Legacy strength program templates (unchanged — keep for existing planner flow)
// ---------------------------------------------------------------------------
export const DRILL_PLAN_TEMPLATES: ProgramTemplateDefinition[] = [
  {
    id: 'push_pull_legs',
    name: 'Push / Pull / Legs',
    dayNames: ['Push Day', 'Pull Day', 'Legs Day'],
    description: 'Three-day split that alternates push, pull, and lower-body emphasis.',
  },
  {
    id: 'upper_lower',
    name: 'Upper / Lower Split',
    dayNames: ['Upper Body', 'Lower Body'],
    description: 'Classic upper / lower split used for twice-per-week structures.',
  },
  {
    id: 'full_body',
    name: 'Full Body',
    dayNames: ['Full Body'],
    description: 'Single-day program template for full-body sessions.',
  },
];

export const DRILL_PLAN_TEMPLATE_MAP: Record<string, ProgramTemplateDefinition> =
  DRILL_PLAN_TEMPLATES.reduce((acc, template) => {
    acc[template.id] = template;
    return acc;
  }, {} as Record<string, ProgramTemplateDefinition>);

export interface ProgramSlotOption {
  dayIndex: number;
  dayName: string;
  slotId: string;
  slotLabel: string;
  existingSlot?: ProgramSlot;
}

export const createEmptySlot = (drillId: string): ProgramSlot => ({
  drillId,
  sets: '',
  reps: '',
  rest: '',
  tempo: '',
  notes: '',
});

export function buildProgramSlotOptions(
  program: Program,
  slotIds: string[] = DEFAULT_SLOT_IDS,
): ProgramSlotOption[] {
  return program.days.flatMap((day, dayIndex) =>
    slotIds.map((slotId) => ({
      dayIndex,
      dayName: day.name,
      slotId,
      slotLabel: program.slotLabels?.[slotId] || slotId,
      existingSlot: day.slots?.[slotId],
    })),
  );
}

export interface ProgramSlotGroup {
  dayIndex: number;
  dayName: string;
  slots: ProgramSlotOption[];
}

export function groupSlotsByDay(slots: ProgramSlotOption[]): ProgramSlotGroup[] {
  const grouped = new Map<number, ProgramSlotGroup>();
  slots.forEach((slot) => {
    const existing = grouped.get(slot.dayIndex);
    if (existing) {
      existing.slots.push(slot);
      return;
    }
    grouped.set(slot.dayIndex, {
      dayIndex: slot.dayIndex,
      dayName: slot.dayName,
      slots: [slot],
    });
  });
  return Array.from(grouped.values());
}

interface UpdateProgramParams {
  program: Program;
  dayIndex: number;
  slotId: string;
  drillId: string;
}

export function updateProgramDaysWithDrill({
  program,
  dayIndex,
  slotId,
  drillId,
}: UpdateProgramParams): Program['days'] {
  return program.days.map((day, index) => {
    if (index !== dayIndex) {
      return day;
    }
    const slots = { ...(day.slots || {}) };
    const existingSlot = slots[slotId];
    slots[slotId] = existingSlot ? { ...existingSlot, drillId } : createEmptySlot(drillId);
    return {
      ...day,
      slots,
    };
  });
}

interface RemoveProgramParams {
  program: Program;
  dayIndex: number;
  slotId: string;
  drillId: string;
}

export function removeDrillFromProgramSlot({
  program,
  dayIndex,
  slotId,
  drillId,
}: RemoveProgramParams): Program['days'] {
  return program.days.map((day, index) => {
    if (index !== dayIndex) {
      return day;
    }

    const slots = { ...(day.slots || {}) };
    const existingSlot = slots[slotId];

    if (!existingSlot) {
      return day;
    }

    if (existingSlot.drillId !== drillId) {
      return day;
    }

    const { [slotId]: _removed, ...remainingSlots } = slots;

    return {
      ...day,
      slots: remainingSlots,
    };
  });
}

export function getNextAvailableSlotId(program: Program): string {
  const existing = new Set<string>([
    ...(program.slotLabels ? Object.keys(program.slotLabels) : []),
    ...program.days.flatMap((day) => Object.keys(day.slots || {})),
  ]);

  const orderedCandidates = [...DEFAULT_SLOT_IDS, ...EXTENDED_SLOT_IDS];
  const candidate = orderedCandidates.find((id) => !existing.has(id));
  if (candidate) {
    return candidate;
  }

  let index = 1;
  while (existing.has(`X${String(index).padStart(2, '0')}`)) {
    index += 1;
  }
  return `X${String(index).padStart(2, '0')}`;
}

export function getDefaultSlotLabel(slotId: string): string {
  if (DEFAULT_SLOT_LABELS[slotId]) {
    return DEFAULT_SLOT_LABELS[slotId];
  }
  return `Slot ${slotId}`;
}

interface TemplateProgramRequest {
  template: ProgramTemplateDefinition;
  clientId: string;
  clientName: string;
  createdBy: string;
  drillId: string;
  targetDayIndex: number;
  targetSlotId: string;
  description?: string;
}

export function buildProgramFromTemplate({
  template,
  clientId,
  clientName,
  createdBy,
  drillId,
  targetDayIndex,
  targetSlotId,
  description = '',
}: TemplateProgramRequest): Omit<Program, 'id' | 'createdAt' | 'updatedAt'> {
  const days = template.dayNames.map((dayName, index) => {
    const slots =
      index === targetDayIndex
        ? {
            [targetSlotId]: createEmptySlot(drillId),
          }
        : {};
    return {
      name: dayName,
      slots,
    };
  });

  return {
    name: `${clientName} – ${template.name}`,
    description,
    days,
    slotLabels: { ...DEFAULT_SLOT_LABELS },
    clientId,
    clientName,
    createdBy,
  };
}

